// Hindi PDF export (B&W and Colour share one layout; only colours differ). Two rows per ghar
// (स.क. row, पाठी row); pages are built per whole ghar so no ghar is split across pages.
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  GHARS,
  WEEKDAYS_HI,
  MONTHS_HI,
  AREA_INFO,
  resolvePreacherHi,
  resolvePathiHi,
  resolveSpecialHi,
  GHAR_TYPE_HI,
  noteLines,
  cellTime,
} from "../data/rssbData";

const esc = (s) => (s == null ? "" : String(s));
// keep the last word with its closing bracket so ")" never wraps alone
const nb = (s) => esc(s).replace(/(\S+\))$/, '<span style="white-space:nowrap">$1</span>');
const fmtDate = (iso) => iso.split("-").reverse().join("/");
const PAGE_W_PX = 1500;

function skHtml(cell, d) {
  if (!cell || !cell.type) return "";
  const tm = `<span class="tm">${cellTime(cell, d?.iso)}</span>`;
  if (cell.type === "CD") return `${tm}<span class="cd">सी डी सत्संग</span>`;
  if (cell.type === "LCD") return `${tm}<span class="cd">सवाल-जवाब सत्संग</span>`;
  if (cell.type === "SPECIAL") return `${tm}<span class="pre">${nb(resolveSpecialHi(cell, d?.iso, false))}</span>`;
  return `${tm}<span class="pre">${nb(resolvePreacherHi(cell))}</span>`;
}
function pathiHtml(cell) {
  if (!cell || !cell.type || cell.type === "CD" || cell.type === "LCD") return "";
  return `<span class="sub">${nb(resolvePathiHi(cell))}</span>`;
}

// Only colours change between modes.
const palette = (bw) =>
  bw
    ? { accent: "#000", line: "#000", soft: "#777", headBg: "#fff", headFg: "#000", off: "#fff", logoBg: "#000" }
    : { accent: "#8B1A1A", line: "#8B1A1A", soft: "#d9b8b8", headBg: "#8B1A1A", headFg: "#fff", off: "#f3eaea", logoBg: "#8B1A1A" };

const style = (c) => `
  <style>
    #pdf-root, #pdf-root * { box-sizing:border-box; }
    #pdf-root table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:13px; }
    #pdf-root th, #pdf-root td { border:1px solid ${c.line}; padding:7px 6px 9px; vertical-align:middle; text-align:center; line-height:1.45; word-wrap:break-word; overflow-wrap:break-word; color:#000; }
    #pdf-root thead th { background:${c.headBg}; color:${c.headFg}; font-weight:700; }
    #pdf-root th .dow { display:block; font-size:12px; font-weight:600; margin-top:3px; border-top:1px solid ${c.headFg}; padding-top:3px; }
    #pdf-root tbody { break-inside:avoid; page-break-inside:avoid; }
    #pdf-root .srno { width:44px; }
    #pdf-root .ghar { width:150px; text-align:left; font-weight:700; padding-left:8px; }
    #pdf-root .role { width:46px; font-weight:600; font-size:12px; }
    #pdf-root .gtype { color:${c.accent}; }
    #pdf-root .off { background:${c.off}; font-weight:700; }
    #pdf-root .pre { font-weight:700; color:#000; }
    #pdf-root .sub { color:#000; }
    #pdf-root .tm { display:block; font-size:11.5px; font-weight:700; color:${c.accent}; margin-bottom:2px; }
    #pdf-root .cd { color:#000; font-style:italic; font-weight:600; }
    #pdf-root td.rowa { border-bottom-color:${c.soft}; }
    #pdf-root td.rowb { border-top-color:${c.soft}; }
    #pdf-root .note { margin-top:14px; font-size:14px; font-weight:600; line-height:1.7; color:#000; }
    #pdf-root .note b { color:${c.accent}; }
  </style>`;

function gharBody(variant, dates, g, i) {
  const td = (d, fn, cls) => {
    const cell = variant.cells[g.id]?.[d.iso];
    if (!cell && !g.days.includes(d.dow)) return `<td class="off ${cls}">XX</td>`;
    return `<td class="${cls}">${fn(cell, d)}</td>`;
  };
  return `<tbody data-ghar="${i}"><tr>
      <td class="srno" rowspan="2">${i + 1}</td>
      <td class="ghar" rowspan="2">${g.hi}<span class="gtype">–${GHAR_TYPE_HI[g.type]}</span></td>
      <td class="role rowa">स.क.</td>
      ${dates.map((d) => td(d, skHtml, "rowa")).join("")}
    </tr><tr><td class="role rowb">पाठी</td>${dates.map((d) => td(d, pathiHtml, "rowb")).join("")}</tr></tbody>`;
}

const headerHtml = (c, year, month) => {
  const sec = [AREA_INFO.secretaryNameHi, AREA_INFO.secretaryDesignationHi, AREA_INFO.phones].filter(Boolean);
  return `
    <div style="display:flex;align-items:flex-start;gap:14px;">
      <div style="background:${c.logoBg};color:#fff;font-weight:800;padding:6px 9px;font-size:20px;line-height:1.15;font-family:Georgia,serif;border:3px double #fff;outline:2px solid ${c.logoBg};">RS<br/>SB</div>
      <div style="flex:1;font-size:22px;font-weight:800;line-height:1.4;padding-top:10px;color:${c.accent};">राधा–स्वामी सत्संग, ब्यास</div>
      ${sec.length ? `<div style="text-align:center;font-size:14px;font-weight:700;line-height:1.55;">${sec.join("<br/>")}</div>` : ""}
    </div>
    <div style="font-size:18px;font-weight:700;line-height:1.5;margin-top:6px;border-bottom:2px solid ${c.accent};padding-bottom:6px;color:${c.accent};">सत्संग प्रोग्राम माह ${MONTHS_HI[month]}–${year} ${AREA_INFO.areaHi} एरिया</div>`;
};

const noteHtml = (month) =>
  `<div class="note"><b>नोट:</b> ${noteLines(month).map((l, i) => `${i + 1}. ${l}`).join("<br/>")}</div>`;

// Full page HTML for the given ghar indices (defaults to all). mode: "color" | "bw"
export function buildTableHtml(variant, dates, year, month, indices = GHARS.map((_, i) => i), mode = "color", withNote = true) {
  const c = palette(mode === "bw");
  const dateCols = dates
    .map((d) => `<th>${fmtDate(d.iso)}<span class="dow">${WEEKDAYS_HI[d.dow]}</span></th>`)
    .join("");
  return `
  <div id="pdf-root" style="width:${PAGE_W_PX}px;background:#fff;color:#000;padding:18px 20px 22px;font-family:'Noto Sans Devanagari','Noto Serif Devanagari',sans-serif;">
    ${headerHtml(c, year, month)}
    <div style="height:12px"></div>
    <table>
      <thead><tr><th class="srno">क्र. सं.</th><th class="ghar" style="text-align:center">सत्संग जगह का नाम</th><th class="role"></th>${dateCols}</tr></thead>
      ${indices.map((i) => gharBody(variant, dates, GHARS[i], i)).join("")}
    </table>
    ${withNote ? noteHtml(month) : ""}
    ${style(c)}
  </div>`;
}

const mount = (html) => {
  const c = document.createElement("div");
  c.style.cssText = "position:fixed;left:-20000px;top:0;";
  c.innerHTML = html;
  document.body.appendChild(c);
  return c;
};

export async function exportPdf(variant, dates, year, month, mode = "color") {
  if (document.fonts?.ready) await document.fonts.ready;
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const margin = 6;
  const imgW = pw - margin * 2;
  const pageHpx = Math.floor(((ph - margin * 2) * PAGE_W_PX) / imgW);

  // Measure once, then group whole ghars into pages; the note goes on the last page.
  const probe = mount(buildTableHtml(variant, dates, year, month, undefined, mode, true));
  const root = probe.querySelector("#pdf-root");
  const bodies = [...root.querySelectorAll("tbody")];
  const noteH = root.querySelector(".note").offsetHeight + 16;
  const fixedH = root.offsetHeight - noteH - bodies.reduce((s, b) => s + b.offsetHeight, 0) + 4;
  const pages = [];
  let cur = [];
  let h = fixedH;
  bodies.forEach((b, i) => {
    if (cur.length && h + b.offsetHeight > pageHpx) {
      pages.push(cur);
      cur = [];
      h = fixedH;
    }
    cur.push(i);
    h += b.offsetHeight;
  });
  if (h + noteH > pageHpx && cur.length > 1) {
    pages.push(cur.slice(0, -1));
    cur = cur.slice(-1);
  }
  if (cur.length) pages.push(cur);
  document.body.removeChild(probe);

  for (let p = 0; p < pages.length; p++) {
    const c = mount(buildTableHtml(variant, dates, year, month, pages[p], mode, p === pages.length - 1));
    try {
      const canvas = await html2canvas(c.querySelector("#pdf-root"), { scale: 2, backgroundColor: "#ffffff" });
      const imgH = Math.min((canvas.height * imgW) / canvas.width, ph - margin * 2);
      if (p > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.9), "JPEG", margin, margin, imgW, imgH, undefined, "FAST");
    } finally {
      document.body.removeChild(c);
    }
  }
  pdf.save(`RSSB_Satsang_${mode === "bw" ? "BW" : "Color"}_${MONTHS_HI[month]}_${year}.pdf`);
}
